import { Authenticator, useAuthenticator, View } from "@aws-amplify/ui-react";
import "@aws-amplify/ui-react/styles.css";
import { signUp, SignUpInput } from "aws-amplify/auth";
import React, { useMemo, useState, useEffect } from "react";
import "./customAuthenticator.css";
import { Loader2, Shield, Lock, Check, Server } from 'lucide-react';
import LogoImage from "../Images/Logo/SymbianLogo.png";
import LogoImageDark from "../Images/Logo/symbian-logo.svg";
import HeroImage from "../Images/auth_hero.png";

interface ICustomAuthenticatorProps {
    authLoading: boolean;
    initialAuthState?: 'signIn' | 'signUp';
}

function useBreakpoint() {
    const [bp, setBp] = useState<'mobile' | 'tablet' | 'desktop'>('desktop');
    useEffect(() => {
        const check = () => {
            const w = window.innerWidth;
            if (w < 640) setBp('mobile');
            else if (w < 1024) setBp('tablet');
            else setBp('desktop');
        };
        check();
        window.addEventListener('resize', check);
        return () => window.removeEventListener('resize', check);
    }, []);
    return bp;
}

const CustomAuthenticator = ({ authLoading, initialAuthState = 'signUp' }: ICustomAuthenticatorProps) => {
    const bp = useBreakpoint();
    const isMobileOrTablet = bp === 'mobile' || bp === 'tablet';

    const services = {
        async handleSignUp(input: SignUpInput) {
            const { username, password, options } = input;
            return signUp({
                username: username.toLowerCase(),
                password,
                options: {
                    ...options,
                    userAttributes: {
                        ...options?.userAttributes,
                        "custom:role": "patient",
                    },
                },
            });
        },
    };

    const formFields = {
        signUp: {
            name: { placeholder: "Enter your full name", label: "Name", order: 1, required: true },
            email: { placeholder: "Enter your email address", label: "Email", order: 2, required: true },
            password: { placeholder: "Create a strong password", label: "Password", order: 3, required: true },
            confirm_password: { placeholder: "Confirm your password", label: "Confirm Password", order: 4, required: true },
            "custom:role": { type: "hidden", value: "patient", order: 5 },
        },
    };

    const authComponent = useMemo(() => (
        <Authenticator
            formFields={formFields}
            initialState={initialAuthState}
            loginMechanisms={["email"]}
            components={CustomComponents}
            services={services}
        />
    ), [initialAuthState]);

    if (isMobileOrTablet) {
        return (
            <div className="auth-mobile-root auth-mobile-premium">
                <div className="auth-mobile-header">
                    <img src={LogoImage} alt="Symbian Health" className="auth-mobile-logo-white" />
                    <div className="auth-mobile-secure-pill">
                        <Shield size={15} />
                        <span>Secure</span>
                    </div>
                </div>

                <div className="auth-mobile-brand-panel">
                    <div className="auth-brand-glow-1" />
                    <div className="auth-brand-glow-2" />
                    <div className="auth-mobile-brand-content">
                        <p className="auth-brand-eyebrow">
                            <span className="auth-brand-eyebrow-dot" />
                              Trusted Second opinion portal
                        </p>
                        <p className="auth-brand-sub">
                            Get expert medical second opinions securely.
                        </p>
                    </div>
                </div>

                <div className="auth-mobile-card-scroll">
                    <div className="auth-mobile-card">
                        {!authLoading ? (
                            <div className="auth-mobile-container auth-fade-in">
                                {authComponent}
                            </div>
                        ) : (
                            <div className="auth-mobile-loading">
                                <div className="auth-loading-icon">
                                    <Loader2 size={24} className="animate-spin" />
                                </div>
                                <p className="auth-loading-text">Setting up your account…</p>
                            </div>
                        )}
                    </div>
                </div>

                <div className="auth-mobile-trust-rail">
                    <span><Shield size={17} /> HIPAA<br />Compliant</span>
                    <span><Lock size={16} /> 256-bit<br />SSL Encryption</span>
                    <span><Server size={17} /> US<br />Servers</span>
                </div>
                <p className="auth-mobile-copyright">© {new Date().getFullYear()} Symbian Health. All rights reserved.</p>
            </div>
        );
    }
    return (
        <div className="auth-root-container h-screen w-full flex overflow-hidden bg-white">
            <div className="hidden lg:block w-[50%] xl:w-[52%] relative overflow-hidden flex-shrink-0 min-w-0">
                <div className="absolute inset-0 wizard-gradient" />
                <img
                    src={HeroImage}
                    alt="Medical professional"
                    className="absolute inset-0 w-full h-full object-cover object-top opacity-35 mix-blend-luminosity"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#011D47]/90 via-[#013976]/55 to-[#024D9A]/40" />
                <div className="absolute inset-0 wizard-mesh opacity-40" />
                <div className="absolute inset-0 wizard-dot-pattern" />
                <div className="absolute -top-20 -right-20 w-96 h-96 bg-brand-400/[0.08] rounded-full blur-[120px] animate-float" />
                <div className="absolute bottom-32 -left-16 w-72 h-72 bg-accent-500/[0.06] rounded-full blur-[100px] [animation-delay:3s]" />
                <div className="absolute top-1/2 right-1/4 w-48 h-48 bg-brand-300/[0.05] rounded-full blur-[80px] [animation-delay:6s]" />

                <div className="auth-left-inner relative z-10 h-full flex flex-col justify-between p-8 xl:p-10">
                    <div className="flex items-center gap-2.5">
                        <img src={LogoImage} alt="Symbian Health" className="h-8 w-auto drop-shadow-lg" />
                    </div>

                    <div className="flex-1 flex flex-col justify-center min-w-0">
                        <h2 className="text-auth-heading xl:text-heading-2-lg font-extrabold text-white tracking-tight leading-tight mb-4 drop-shadow-sm">
                            Why I Need a Medical<br />Second Opinion?
                        </h2>
                        <div className="auth-feature-list space-y-2">
                            {[
                                { title: 'Confirm My Diagnosis', desc: 'Ensure I have the right diagnosis.' },
                                { title: 'Explore Treatment Options', desc: 'Find the best fit for my condition.' },
                                { title: 'Avoid Risks', desc: 'Steer clear of unnecessary treatments.' },
                                { title: 'Consult Experts', desc: 'Get insights from top specialists.' },
                                { title: 'Convenient & Private', desc: 'Expert advice from home.' },
                                { title: 'Save Time & Money', desc: 'Make smarter, quicker decisions.' },
                                { title: 'Take Control', desc: 'Empower myself with informed choices.' },
                                { title: 'Improve Outcomes', desc: 'Increase my chances for better recovery.' },
                            ].map((item, i) => (
                                <div key={i} className="flex items-start gap-3 group">
                                    <div className="w-5 h-5 rounded-full bg-white/[0.12] flex items-center justify-center flex-shrink-0 mt-0.5 border border-white/[0.08] shadow-[0_0_8px_rgba(255,255,255,0.04)]">
                                        <Check size={10} strokeWidth={2.5} className="text-white/90" />
                                    </div>
                                    <p className="text-sm text-white/55 leading-relaxed">
                                        <span className="font-semibold text-white/90">{item.title}:</span>{' '}{item.desc}
                                    </p>
                                </div>
                            ))}
                        </div>
                    </div>

                    <div className="auth-stats-bar flex items-center gap-6 bg-white/[0.05] backdrop-blur-glass rounded-2xl px-6 py-3.5 border border-white/[0.06] shadow-[0_8px_32px_-8px_rgba(0,0,0,0.3)]">
                        {[
                            { val: '50+', label: 'Specialists' },
                            { val: '99%', label: 'Satisfaction' },
                            { val: '48h', label: 'Response' },
                        ].map((s, i) => (
                            <React.Fragment key={i}>
                                {i > 0 && <div className="w-px h-6 bg-white/[0.08]" />}
                                <div className="text-center flex-1 min-w-0">
                                    <p className="text-sm font-bold text-white/95 drop-shadow-sm truncate">{s.val}</p>
                                    <p className="text-nav-xs text-white/45 font-medium truncate">{s.label}</p>
                                </div>
                            </React.Fragment>
                        ))}
                    </div>
                </div>
            </div>
            <div className="auth-patient-right-panel w-[50%] xl:w-[48%] h-full flex flex-col relative bg-surface-50 min-w-0 overflow-hidden">
                <div className="h-[3px] w-full bg-gradient-to-r from-brand-400 via-brand-500 to-brand-600 flex-shrink-0" />
                <div className="auth-scroll-container flex-1 min-h-0 flex flex-col justify-center items-center overflow-hidden">
                    {!authLoading ? (
                        <div className="auth-custom-container auth-patient-form-shell auth-fade-in w-full min-w-0">
                            {authComponent}
                        </div>
                    ) : (
                        <div className="flex flex-col justify-center items-center gap-4">
                            <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-brand-50 to-brand-100 flex items-center justify-center shadow-elevation-2 ring-1 ring-brand-500/[0.06]">
                                <Loader2 size={26} className="animate-spin text-brand-500" />
                            </div>
                            <div className="text-center">
                                <p className="text-sm text-slate-600 font-bold">Setting up your account</p>
                                <p className="text-compact text-slate-400 mt-1">This will only take a moment…</p>
                            </div>
                        </div>
                    )}
                </div>

                <div className="px-6 py-3 border-t border-brand-500/[0.04] bg-white/40 backdrop-blur-sm flex items-center justify-between flex-shrink-0">
                    <div className="flex items-center gap-3">
                        <div className="flex items-center gap-1.5 bg-brand-500/[0.03] px-2 py-1 rounded-md">
                            <Shield size={11} className="text-brand-500/50" />
                            <span className="text-nav-xs text-slate-400 font-semibold">HIPAA</span>
                        </div>
                        <div className="w-px h-3 bg-brand-500/[0.06]" />
                        <div className="flex items-center gap-1.5 bg-brand-500/[0.03] px-2 py-1 rounded-md">
                            <Lock size={10} className="text-brand-500/50" />
                            <span className="text-nav-xs text-slate-400 font-semibold">256-bit</span>
                        </div>
                        <div className="w-px h-3 bg-brand-500/[0.06]" />
                        <p className="text-nav-xs text-slate-400 font-medium">
                            © {new Date().getFullYear()} Symbian Health
                        </p>
                    </div>
                    <div className="flex items-center gap-3">
                        <a href="/privacy-policy" className="text-nav-xs text-slate-400 hover:text-brand-500 transition-colors font-medium">Privacy</a>
                        <a href="/terms-conditions" className="text-nav-xs text-slate-400 hover:text-brand-500 transition-colors font-medium">Terms</a>
                    </div>
                </div>
            </div>
        </div>
    );
};

const CustomComponents = {
    SignIn: {
        Header() {
            return (
                <div className="auth-form-header px-5 pt-5 pb-3">
                    <h3 className="text-auth-heading font-extrabold text-navy-500 tracking-tight">Welcome Back</h3>
                    <p className="text-sm text-slate-400 mt-1.5">Sign in to your medical portal</p>
                </div>
            );
        },
        Footer() {
            const { toForgotPassword, toSignUp } = useAuthenticator();
            return (
                <View style={{ display: "flex", flexWrap: "wrap", justifyContent: "space-between", width: "100%", padding: "4px 0 8px" }}>
                    <CustomLink onClick={toForgotPassword}>Forgot Password?</CustomLink>
                    <CustomLink onClick={toSignUp}>
                        New here? <span className="font-bold text-brand-600 ml-0.5">Create Account</span>
                    </CustomLink>
                </View>
            );
        },
    },
    SignUp: {
        Header() {
            return (
                <div className="auth-form-header px-5 pt-5 pb-3">
                    <h3 className="text-auth-heading font-extrabold text-navy-500 tracking-tight">Create Account</h3>
                    <p className="text-sm text-slate-400 mt-1.5">Join our secure second opinion platform</p>
                </div>
            );
        },
        Footer() {
            const { toSignIn } = useAuthenticator();
            return (
                <View style={{ display: "flex", justifyContent: "center", alignItems: "center", width: "100%", padding: "4px 0 8px" }}>
                    <CustomLink onClick={toSignIn}>
                        Already have an account? <span className="font-bold text-brand-600 ml-1">Sign In</span>
                    </CustomLink>
                </View>
            );
        },
    },
};

const CustomLink = ({ onClick, children }: { onClick: () => void, children: React.ReactNode }) => (
    <button
        onClick={onClick}
        className="text-slate-400 hover:text-brand-600 hover:bg-brand-50/60 px-3 py-2 rounded-xl text-sm font-medium transition-all duration-300 ease-[cubic-bezier(0.22,1,0.36,1)] min-h-[44px]"
    >
        {children}
    </button>
);

export default CustomAuthenticator;
